import { Card } from "@/components/flexkit/Card";
import type { PlainWorkflowExecution } from "@/queries/automation-queries";
import type { FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { durationBetweenTimestamps, timeAgo } from "@/format/time";
import { Timestamp } from "@bufbuild/protobuf";
import { WorkflowExecutionStatusPill } from "@/components/automations/AutomationExecutionStatusPill";
import { WorkflowExecutionActionsDropdown } from "@/components/automations/AutomationExecutionActionsDropdown";
import IconCalendar2 from "central-icons/IconCalendar2";
import IconStopwatch from "central-icons/IconStopwatch";
import IconBranch from "central-icons/IconBranch";
import IconTriangleExclamation from "central-icons/IconTriangleExclamation";

type WorkflowExecutionsCardProps = {
    execution: PlainWorkflowExecution;
    readonly?: boolean;
};

export const WorkflowExecutionsCard: FC<WorkflowExecutionsCardProps> = ({ execution, readonly = false }) => {
    const duration =
        execution.metadata?.startedAt && execution.metadata?.finishedAt
            ? durationBetweenTimestamps(execution.metadata.startedAt, execution.metadata.finishedAt)
            : undefined;

    const totalActionsCount =
        (execution.status?.doneActionCount ?? 0) +
        (execution.status?.runningActionCount ?? 0) +
        (execution.status?.pendingActionCount ?? 0) +
        (execution.status?.failedActionCount ?? 0);

    return (
        <Card key={execution.id} variant={"bordered"} className="border bg-surface-secondary shadow-sm">
            <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1">
                    <Text className="text-base font-medium text-content-primary">{execution.id}</Text>
                    <WorkflowExecutionStatusPill execution={execution} />
                </div>
                {!readonly && <WorkflowExecutionActionsDropdown execution={execution} />}
            </div>
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                    <IconCalendar2 size={16} className="text-content-muted" />
                    <Text className="text-sm text-content-muted">
                        {execution.metadata?.startedAt ? (
                            timeAgo(new Timestamp(execution.metadata.startedAt).toDate())
                        ) : (
                            "-"
                        )}
                    </Text>
                </div>
                <div className="flex items-center gap-1">
                    <IconStopwatch size={16} className="text-content-muted" />
                    <Text className="text-sm text-content-muted">
                        {duration ? (
                            <span>{duration}</span>
                        ) : execution.metadata?.startedAt ? (
                            <span>In progress</span>
                        ) : (
                            <span>-</span>
                        )}
                    </Text>
                </div>
                <div className="flex items-center gap-1">
                    <IconBranch size={16} className="text-content-muted" />
                    <Text className="text-sm text-content-muted">{totalActionsCount} actions</Text>
                </div>
                {execution.status?.failedActionCount && execution.status?.failedActionCount > 0 ? (
                    <div className="flex items-center gap-1">
                        <IconTriangleExclamation size={16} className="text-content-muted" />
                        <Text className="text-sm text-content-muted">
                            {execution.status?.failedActionCount} <span>errors</span>
                        </Text>
                    </div>
                ) : undefined}
            </div>
        </Card>
    );
};
